import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Checkbox, Divider, Link } from '@mui/material';
import styles from '../header/Header.module.css';
import NavLinkNewTab from '../commons/NavLinkNewTab';
import { ReportData, PibDetail } from '../../model/reportData';
import { useState } from 'react';
import { Order, sortReportByField } from './ReportService';
import { SortableTableHeader } from './SortableTableHeader';
import {
  emCompleteEventDetailsHeadersMobile,
  eventHeadersMobile,
  pictureDayHeadersMobile,
  quarantineEventHeadersMobile
} from '../../model/report-fields';
import { green, red } from '@mui/material/colors';
import { ReportTableType } from '../../model/reportTableType';

interface IProps {
  tableData: ReportData[];
  tableName: ReportTableType;
  quarantineFix?: boolean;
  selectedEventId?: (eventId: string) => void;
}
export default function ReportTableMobile({ tableData, tableName, quarantineFix, selectedEventId = () => { } }: IProps) {
  const [order, setOrder] = useState<Order>('asc');

  // By default we will display in sorted order of last event date
  const [orderBy, setOrderBy] = useState<keyof ReportData>(
    tableName === ReportTableType.EM_COMPLETION_STATUS ? 'photographyEndTime': (tableName === ReportTableType.PICTURE_DAY) ? 'firstPictureDay' : 'lastPictureDay'
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRow, setSelectedRow] = React.useState<number>();

  const handleSelectEvent = (index: any, eventId: string) => {
    if (selectedRow === index) {
      setSelectedRow(undefined);
      selectedEventId('');
    } else {
      setSelectedRow(index);
      selectedEventId(eventId);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ReportData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer>
        <Table sx={{ maxWidth: 700 }} aria-labelledby="tableTitle" size={'small'}>

          {(tableName === ReportTableType.EVENT ||
            tableName === ReportTableType.NOT_UPLOADED_EVENT) && (
              <SortableTableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={tableData.length}
                headCells={eventHeadersMobile}
              />
            )}

          {tableName === ReportTableType.PICTURE_DAY && (
            <SortableTableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={tableData.length}
              headCells={pictureDayHeadersMobile}
            />
          )}

          {tableName === ReportTableType.QUARANTINE_EVENT && (
            <SortableTableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={tableData.length}
              headCells={quarantineEventHeadersMobile}
              quarantineFix={quarantineFix}
            />
          )}

          {tableName === ReportTableType.EM_COMPLETION_STATUS && (
            <SortableTableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={tableData.length}
              headCells={emCompleteEventDetailsHeadersMobile}
            />
          )}
          <TableBody>
            {tableData
              .slice()
              .sort(sortReportByField(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.eventId + index}>
                    <TableCell data-testid="event" sx={{ color: '#3593D0', pl: 3 }} align="left">
                      <Link
                        underline="always"
                        component={NavLinkNewTab}
                        to={'/event-details/' + row.eventId}
                        activeClassName={styles.active}
                        className={styles.mobileMenuLink}
                      >
                        {row.eventId}
                      </Link>
                    </TableCell>
                    {tableName !== ReportTableType.EM_COMPLETION_STATUS && (
                      <TableCell data-testid="area" align="left" sx={{ pl: 1 }}>
                      {row.area}
                    </TableCell>
                    )}
                    {tableName === ReportTableType.EM_COMPLETION_STATUS && (
                      <TableCell data-testid="photographyEndTime" align="left" sx={{ pl: 1 }}>
                        {row.photographyEndTime}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.EM_COMPLETION_STATUS && (
                      <TableCell data-testid="status" align="center" sx={{ pl: 1 }}>
                        {row.status !== 'EM Complete' && <Box sx={{ bgcolor: red[500], fontWeight: 'bold' }}>{row.status}</Box>}

                        {row.status === 'EM Complete' && <Box sx={{ bgcolor: green[500], fontWeight: 'bold' }}>{row.status}</Box>}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="imageCount" align="left" sx={{ pl: 1 }}>
                        {row.imageCount}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="pibCount" align="left" sx={{ p: 0 }}>
                        {row.pibCount}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="uploadDate" align="left" sx={{ p: 0 }}>
                        {row.uploadDate}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="photographerId" align="left" sx={{ p: 0 }}>
                        {row.pibDetails && row.pibDetails.length > 1 && row.pibDetails.some((pib: PibDetail) => !!pib.photographerId)
                          ? row.pibDetails.map((pib: PibDetail, i: number) => (
                              <Box key={i}>
                                {i > 0 && <Divider sx={{ my: 0.5 }} />}
                                <Box>{pib.photographerId}</Box>
                              </Box>
                            ))
                          : row.photographerId
                        }
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="cameraSerialNumber" align="left" sx={{ p: 0 }}>
                        {row.pibDetails && row.pibDetails.length > 1
                          ? row.pibDetails.map((pib: PibDetail, i: number) => (
                              <Box key={i}>
                                {i > 0 && <Divider sx={{ my: 0.5 }} />}
                                <Box>{pib.cameraSerial}</Box>
                              </Box>
                            ))
                          : row.cameraSerialNumber
                        }
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="eventScheduledDate" align="left" sx={{ p: 0 }}>
                        {row.eventScheduledDate}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="numberOfOrders" align="left" sx={{ p: 0 }}>
                        {row.pibDetails && row.pibDetails.length > 1
                          ? row.pibDetails.map((pib: PibDetail, i: number) => (
                              <Box key={i}>
                                {i > 0 && <Divider sx={{ my: 0.5 }} />}
                                <Box>{pib.numberOfOrders}</Box>
                              </Box>
                            ))
                          : row.numberOfOrders
                        }
                      </TableCell>
                    )}

                    {(tableName !== ReportTableType.PICTURE_DAY && tableName !== ReportTableType.QUARANTINE_EVENT) && (
                      <TableCell data-testid="lastPictureDay" align="left" sx={{ pl: 3 }}>
                        {row.lastPictureDay}
                      </TableCell>
                    )}

                    {tableName === ReportTableType.PICTURE_DAY && (
                      <TableCell data-testid="firstPictureDay" align="left" sx={{ pl: 0 }}>
                        {row.firstPictureDay}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.PICTURE_DAY && (
                      <TableCell data-testid="status" align="center" sx={{ pl: 1 }}>
                        {row.status === 'Not Connected/Started' && (
                          <Box sx={{ bgcolor: red[500] }}>{'Not Started'}</Box>
                        )}

                        {row.status !== 'Not Connected/Started' && <Box sx={{ bgcolor: green[500] }}>{'Started'}</Box>}
                      </TableCell>
                    )}
                    {tableName === ReportTableType.QUARANTINE_EVENT && (
                      <TableCell data-testid="status" align="center" sx={{ pl: 1 }}>
                        {row.status === 'IN_PROGRESS' ? 'IN PROGRESS' : row.status}
                      </TableCell>
                    )}
                    {<TableCell align="center" sx={{ pl: 1 }}>
                      {(quarantineFix && (row.status !== 'COMPLETED' && row.status !== 'RESOLVED')) && <Checkbox sx={{ padding: 0 }} checked={selectedRow === index} onClick={() => handleSelectEvent(index, row.eventId)} />}
                    </TableCell>}
                  </TableRow>
                );
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows,
                }}
              >
                <TableCell colSpan={1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={tableData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}